import * as React from 'react';
import { cn } from '../lib/cn';

/**
 * KeyValue (DataPanel) — read-mode key/value list for order detail panels.
 *
 * Vertical (default): label above value, dense.
 * Horizontal: label left/start, value right/end (logical, RTL-correct).
 *
 * For edit-in-place behavior, wrap a value in a clickable Edit button — the
 * panel itself is presentation-only (single-responsibility).
 */

export interface KeyValueItemProps {
  label: React.ReactNode;
  value: React.ReactNode;
  hint?: React.ReactNode;
}

export interface KeyValueProps extends React.HTMLAttributes<HTMLDListElement> {
  items: KeyValueItemProps[];
  layout?: 'vertical' | 'horizontal';
  divided?: boolean;
}

export function KeyValue({
  items,
  layout = 'vertical',
  divided = true,
  className,
  ...props
}: KeyValueProps) {
  return (
    <dl className={cn('w-full', className)} {...props}>
      {items.map((item, idx) => (
        <div
          key={idx}
          className={cn(
            layout === 'horizontal'
              ? 'flex items-baseline justify-between gap-4 py-2'
              : 'flex flex-col gap-1 py-2',
            divided && idx > 0 && 'border-t border-border-subtle',
          )}
        >
          <dt className="text-xs font-medium text-text-muted uppercase tracking-wide">
            {item.label}
          </dt>
          <dd
            className={cn(
              'text-sm text-text-primary',
              layout === 'horizontal' && 'text-end',
            )}
          >
            {item.value}
            {item.hint ? (
              <div className="text-xs text-text-muted mt-0.5">{item.hint}</div>
            ) : null}
          </dd>
        </div>
      ))}
    </dl>
  );
}
